import { Dispatch, SetStateAction } from "react";
import { AddedClusters, ImportProcessData, importProcessDataTemplate } from "../models/ImportProcessData";
import APIService, { APIServiceFormat } from "../APIs/APIService";
import { ECUCataSelectObjectInterface, ecucCataSelectObjTemplate } from "../models/ECUCInterfaces";
import { hasNonEmptyUnfilteredProgramOptions, resolveProgramOptionsWithFallback } from "./programOptionsUtils";
import { getSelectionCriteriaList } from "./ecuCatalogeUtils";
import { FNV3X_ARCHITECTURES } from "./constants";

export async function handleImportSRPd({
    data, setImportProcessData, setShowModal, apiService, setIsImportErrorCaught, ecuCataSelectObj, setECUCataSelectObj
}: Readonly<{
    data: any,
    setImportProcessData: Dispatch<SetStateAction<ImportProcessData | null>>,
    setShowModal: Dispatch<SetStateAction<boolean>>,
    apiService: APIService,
    setIsImportErrorCaught: Dispatch<SetStateAction<boolean>>,
    ecuCataSelectObj: ECUCataSelectObjectInterface,
    setECUCataSelectObj: Dispatch<SetStateAction<ECUCataSelectObjectInterface>>,
}>) {
    try {
        const importProcessData = structuredClone(importProcessDataTemplate);
        importProcessData.enabled = true;
        // Process the imported data as needed
        const validatedReleaseDataResult = await validateReleaseData({data, apiService, ecuCataSelectObj});
        if (validatedReleaseDataResult.errorMessages.length > 0) {
            setImportProcessData({
                ...importProcessData,
                registeredDataErrorMessages: validatedReleaseDataResult.errorMessages,
                unRegisteredClusters: validatedReleaseDataResult.unRegisteredClusters
            });
            setShowModal(false);
            return;
        }
        const validatedNonOverdriveMetadataResult = await validateNonOverdriveMetadata({
            data,
            ecuCataSelectObj: validatedReleaseDataResult.ecuCataSelectObjFromImport,
            unregisteredClusters: validatedReleaseDataResult.unRegisteredClusters,
            apiService
        });
        if (validatedNonOverdriveMetadataResult.errorMessages.length > 0) {
            setImportProcessData({
                ...importProcessData,
                failedMetadataErrorMessages: validatedNonOverdriveMetadataResult.errorMessages,
                unRegisteredClusters: validatedReleaseDataResult.unRegisteredClusters
            });
            setShowModal(false);
            return;
        }

        // Populate importProcessData.addedClusters
        const addedClusters: AddedClusters[] = [];
        const machineList = data.machineList || [];
        populateAddedClusters(machineList, validatedReleaseDataResult, addedClusters);

        // remove unRegisteredClusters from data before setting ECUCataSelectObj
       for (const machine of data.machineList) {
            const machineVariants = machine.variantList || [];
            for (const variant of machineVariants) {
                variant.clusterList = variant.clusterList.filter((cluster: any) => 
                    !validatedReleaseDataResult.unRegisteredClusters.some((unregCluster) =>
                        unregCluster.clusterName === cluster.clusterName &&
                        unregCluster.variantName === variant.variantName &&
                        unregCluster.machineName === machine.machineName));
            }
        }

        setECUCataSelectObj(validatedReleaseDataResult.ecuCataSelectObjFromImport);
        setImportProcessData({
            ...importProcessData,
            addedClusters: addedClusters,
            data: data,
            unRegisteredClusters: validatedReleaseDataResult.unRegisteredClusters
        });

        setShowModal(false);
    } catch (e) {
        setIsImportErrorCaught(true);
        console.error("Error processing imported SRPd data:", e);
    }

    function populateAddedClusters(machineList: any, validatedReleaseDataResult: { errorMessages: string[]; unRegisteredClusters: Record<string, any>[]; ecuCataSelectObjFromImport: any; }, addedClusters: AddedClusters[]) {
        for (let machineIndex = 0; machineIndex < machineList.length; machineIndex++) {
            const machine = machineList[machineIndex];
            const variantList = machine.variantList || [];
            for (let variantIndex = 0; variantIndex < variantList.length; variantIndex++) {
                const variant = variantList[variantIndex];
                const clusterList = variant.clusterList || [];
                for (const cluster of clusterList) {
                    const isClusterUnregistered = validatedReleaseDataResult.unRegisteredClusters.some((unregCluster) => unregCluster.clusterName === cluster.clusterName &&
                        unregCluster.variantName === variant.variantName &&
                        unregCluster.machineName === machine.machineName
                    );
                    if (!isClusterUnregistered) {
                        addedClusters.push({
                            clusterName: cluster.clusterName,
                            machineIndex: machineIndex,
                            variantIndex: variantIndex
                        });
                    }
                }
            }
        }
    }
}


async function validateReleaseData ({data, apiService, ecuCataSelectObj}:
    Readonly<{
        data: any,
        apiService: APIService,
        ecuCataSelectObj: ECUCataSelectObjectInterface
    }>
): Promise<{
    ecuCataSelectObjFromImport: ECUCataSelectObjectInterface,
    errorMessages: string[];
    unRegisteredClusters: Record<string, any>[];
}> {
    try {
        const ecuCataSelectObjFromImport = structuredClone(ecucCataSelectObjTemplate);
        const hasExistingUnfilteredOptions = hasNonEmptyUnfilteredProgramOptions(ecuCataSelectObj.programs);
        const fallbackUnfilteredOptions = resolveProgramOptionsWithFallback(ecuCataSelectObj.programs);

        ecuCataSelectObjFromImport.programs.unfilteredOptions = structuredClone(fallbackUnfilteredOptions);

        const returnTemplate = {
            errorMessages: [],
            unRegisteredClusters: [],
            ecuCataSelectObjFromImport: ecuCataSelectObjFromImport
        }

        // Implement validation logic for release data here
        // Return true if valid, false otherwise

        ///////// VALIDATE VEHICLE PROGRAMS ////////
        const vehiclePrograms = data.moduleInformation.vehiclePrograms || [];
        const { validProgramData, validProgramOptions } = await getValidProgramOptionsAndData();

        if (!hasExistingUnfilteredOptions) {
            ecuCataSelectObjFromImport.programs.unfilteredOptions = structuredClone(validProgramOptions);
        }

        /**
         * Transform program data to options with modelYearProgramCode as label
         * Map each program data entry back to its corresponding program ID from validProgramOptions
         */
        const validProgramOptionsWithModelYear = getValidProgramOptionsWithModelYearProgramCode(validProgramData, validProgramOptions);

        const invalidPrograms = vehiclePrograms.filter((program: string) =>  
            !validProgramOptionsWithModelYear.some((option: { label: string; value: string }) => program === option.label));

        if (invalidPrograms.length > 0) {
            console.log("Invalid vehicle programs found:", invalidPrograms);
            return {
                ...structuredClone(returnTemplate),
                errorMessages: [`Invalid vehicle programs found: ${invalidPrograms.join(", ")}`],
                unRegisteredClusters: []
            }
        }

        const programIds: string[] = populateProgramIdsList(validProgramOptionsWithModelYear, vehiclePrograms);
        const mismatchingArchitectureMessage = getMismatchingArchitectureMessage(
            validProgramOptions,
            programIds
        );
        if (mismatchingArchitectureMessage) {
            return {
                ...structuredClone(returnTemplate),
                errorMessages: [mismatchingArchitectureMessage],
                unRegisteredClusters: []
            }
        }

        // Populate program options and value to ecuCataSelectObjFromImport
        populateProgramDataToEcuCataSelectObjFromImport(validProgramOptions, programIds, ecuCataSelectObjFromImport);


        //////// VALIDATE MODULE NAME ////////
        const moduleName = data.moduleInformation.moduleName;
        const validModuleOptionsResponse = await apiService.getModuleOptions(programIds)
        if (!validModuleOptionsResponse.isSuccess) {
            throw new Error("Failed to fetch valid module names for validation.");
        }
        const validModuleOptions = validModuleOptionsResponse.data || [];
        const isValidModule = validModuleOptions.some((option: { label: string; value: string }) => moduleName === option.label);
        if (!isValidModule) {
            return {
                ...structuredClone(returnTemplate),
                errorMessages: [`Invalid module name found: ${moduleName}`],
                unRegisteredClusters: []
            }
        }
        const moduleId = validModuleOptions.find((option: { label: string; value: string }) => moduleName === option.label)?.value;

        // Populate module options and value to ecuCataSelectObjFromImport
        validModuleOptions.forEach((option: { label: string; value: string }) => {
            if (moduleId === option.value) {
                ecuCataSelectObjFromImport.module.value = option;
            }
            ecuCataSelectObjFromImport.module.options.push(option);
        });


        //////// VALIDATE MACHINE NAME ////////
        const machineNames = [];
        const machineData = data.machineList || [];
        const validMachineOptions = await getValidMachineOptions(machineData, machineNames, programIds, moduleId);
        const invalidMachines = machineNames.filter((machineName: string) => 
            !validMachineOptions.some((option: { label: string; value: string }) => machineName === option.label));

        if (invalidMachines.length > 0) {
            return {
                ...structuredClone(returnTemplate),
                errorMessages: [`Invalid machine names found: ${invalidMachines.join(", ")}`],
                unRegisteredClusters: []
            }
        }

        const machineIds: string[] = getValidMachineIds(validMachineOptions, machineNames);

        // Populate machine options and value to ecuCataSelectObjFromImport
        populateMachineDataToEcuCataSelectObjFromImport(validMachineOptions, machineIds, ecuCataSelectObjFromImport);

        //////// VALIDATE VARIANT NAME ////////
        const machineToVariantMap = getMachineToVariantMap(machineData);

        // Fetch valid variant options for all machines
        const invalidVariantsOverall = [];
        const validVariantOptionsOverall: { label: string; value: string, machineId: string }[] = [];
        await processInvalidAndValidVariantsLists(machineToVariantMap, validMachineOptions, programIds, moduleId, validVariantOptionsOverall, invalidVariantsOverall);   
        if (invalidVariantsOverall.length > 0) {
            const errorMessages = invalidVariantsOverall.map((entry: any) => {
                const machineName = Object.keys(entry)[0];
                const variants = entry[machineName];
                return `Invalid variant names for machine ${machineName}: ${variants.join(", ")}`;
            });
            return {
                ...structuredClone(returnTemplate),
                errorMessages: errorMessages,
                unRegisteredClusters: []
            }
        }

        // Populate variant options and value to ecuCataSelectObjFromImport
        populateVariantDataToEcuCataSelectObjFromImport(validVariantOptionsOverall, machineToVariantMap, ecuCataSelectObjFromImport);

        let unregisteredClusters: Record<string, any>[] = [];


        //////// VALIDATE CLUSTER NAME ////////
        await populateUnregisteredClusters(machineData, validMachineOptions, validVariantOptionsOverall, programIds, moduleId, machineIds, unregisteredClusters);
        return {
            ...structuredClone(returnTemplate),
            errorMessages: [],
            unRegisteredClusters: unregisteredClusters
        }
    } catch (e) {
        console.error("Error validating release data:", e);
        throw e;
    }

    async function populateUnregisteredClusters(machineData: any, validMachineOptions: any, validVariantOptionsOverall: { label: string; value: string; machineId: string; }[], programIds: string[], moduleId: any, machineIds: string[], unregisteredClusters: Record<string, any>[]) {
        for (const machine of machineData) {
            const machineId = validMachineOptions.find(
                (option: { label: string; value: string }) => option.label === machine.machineName
            )?.value;
            const machineVariants = machine.variantList || [];
            for (const variant of machineVariants) {
                const clusters = variant.clusterList || [];
                const variantId = validVariantOptionsOverall.find((option: { label: string; value: string; machineId: string; }) =>
                    option.machineId === machineId && option.label === variant.variantName
                );
                const validClusterOptionsResponse = await apiService.getClusterOptions(programIds, moduleId, machineIds, [variantId]);
                if (!validClusterOptionsResponse.isSuccess) {
                    throw new Error("Failed to fetch valid cluster names for validation.");
                }
                const validClusterOptions = validClusterOptionsResponse.data || [];
                const invalidClusters = clusters.filter((cluster: any) => !validClusterOptions.some((option: { label: string; value: string; }) => cluster.clusterName === option.label));

                if (invalidClusters.length > 0) {
                    invalidClusters.forEach((cluster: any) => {
                        unregisteredClusters.push({
                            clusterName: cluster.clusterName,
                            variantName: variant.variantName,
                            machineName: machine.machineName,
                            variantId: variantId?.value || ""
                        });
                    });
                }
            }
        }
    }

    function populateVariantDataToEcuCataSelectObjFromImport(validVariantOptionsOverall: { label: string; value: string; machineId: string; }[], machineToVariantMap: Map<string, string[]>, ecuCataSelectObjFromImport: ECUCataSelectObjectInterface) {
        validVariantOptionsOverall.forEach((option: { label: string; value: string; machineId: string; }) => {
            const matchingMachine = ecuCataSelectObjFromImport.machines.value.find(
                (machine) => machine.value === option.machineId
            );
            if (matchingMachine && machineToVariantMap.get(matchingMachine.label)?.includes(option.label)) {
                ecuCataSelectObjFromImport.variants.value.push(option);
            }
            ecuCataSelectObjFromImport.variants.options.push(option);
        });
    }

    async function processInvalidAndValidVariantsLists(machineToVariantMap: Map<string, string[]>, validMachineOptions: any, programIds: string[], moduleId: any, validVariantOptionsOverall: { label: string; value: string; machineId: string; }[], invalidVariantsOverall: any[]) {
        for (const [machineName, variantNames] of machineToVariantMap.entries()) {
            const validVariantOptions = await getValidVariantOptions(validMachineOptions, machineName, programIds, moduleId);
            validVariantOptionsOverall.push(...validVariantOptions);

            const invalidVariants = variantNames.filter((variantName: string) => !validVariantOptions.some((option: { label: string; value: string; }) => variantName === option.label));

            if (invalidVariants.length > 0) {
                invalidVariantsOverall.push({
                    [machineName]: invalidVariants
                });
            }
        }
    }

    async function getValidVariantOptions(validMachineOptions: any, machineName: string, programIds: string[], moduleId: any) {
        const machineId = validMachineOptions.find((option: { label: string; value: string; }) => machineName === option.label)?.value;
        const validVariantOptionsResponse = await apiService.getVariantOptions(programIds, moduleId, [machineId]);
        if (!validVariantOptionsResponse.isSuccess) {
            throw new Error("Failed to fetch valid variant names for validation.");
        }
        return (validVariantOptionsResponse.data || []).map((option: { label: string; value: string; }) => ({
            ...option,
            machineId
        }));
    }

    function getMachineToVariantMap(machineData: any) {
        const machineToVariantMap = new Map<string, string[]>();
        machineData.forEach((machine: any) => {
            const machineVariants = machine.variantList || [];
            const variantNames: string[] = [];
            machineVariants.forEach((variant: any) => {
                if (variant.variantName) {
                    variantNames.push(variant.variantName);
                }
            });
            machineToVariantMap.set(machine.machineName, variantNames);
        });
        return machineToVariantMap;
    }

    function populateMachineDataToEcuCataSelectObjFromImport(validMachineOptions: any, machineIds: string[], ecuCataSelectObjFromImport: ECUCataSelectObjectInterface) {
        validMachineOptions.forEach((option: { label: string; value: string; }) => {
            if (machineIds.includes(option.value)) {
                ecuCataSelectObjFromImport.machines.value.push(option);
            }
            ecuCataSelectObjFromImport.machines.options.push(option);
        });
    }

    function getValidMachineIds(validMachineOptions: any, machineNames: any[]) {
        const machineIds: string[] = [];
        validMachineOptions.forEach((option: { label: string; value: string; }) => {
            if (machineNames.includes(option.label)) {
                machineIds.push(option.value);
            }
        });
        return machineIds;
    }

    async function getValidMachineOptions(machineData: any, machineNames: any[], programIds: string[], moduleId: any) {
        machineData.forEach((machine: any) => {
            if (machine.machineName) {
                machineNames.push(machine.machineName);
            }
        });

        const validMachineOptionsResponse = await apiService.getMachineOptions(programIds, moduleId);
        if (!validMachineOptionsResponse.isSuccess) {
            throw new Error("Failed to fetch valid machine names for validation.");
        }
        const validMachineOptions = validMachineOptionsResponse.data || [];
        return validMachineOptions;
    }

    function populateProgramDataToEcuCataSelectObjFromImport(validProgramOptions: any, programIds: string[], ecuCataSelectObjFromImport: ECUCataSelectObjectInterface) {
        validProgramOptions.forEach((option: { label: string; value: string; }) => {
            if (programIds.includes(option.value)) {
                ecuCataSelectObjFromImport.programs.value.push(option);
            }
            ecuCataSelectObjFromImport.programs.options.push(option);
        });
    }

    function getMismatchingArchitectureMessage(validProgramOptions: any, programIds: string[]): string | null {
        const selectedProgramOptions = validProgramOptions.filter(
            (option: { value: string }) => programIds.includes(option.value)
        );
        if (selectedProgramOptions.some(
            (option: { electricalArchitecture?: string }) => !option.electricalArchitecture
        )) {
            return null;
        }

        const programsByArchitecture = new Map<string, string[]>();

        selectedProgramOptions
            .forEach((option: { label: string; electricalArchitecture: string }) => {
                const architecture = FNV3X_ARCHITECTURES.includes(option.electricalArchitecture)
                    ? "FNV3.x"
                    : option.electricalArchitecture;
                const programs = programsByArchitecture.get(architecture) || [];
                programs.push(option.label);
                programsByArchitecture.set(architecture, programs);
            });

        if (programsByArchitecture.size <= 1) {
            return null;
        }

        const architectureDetails = Array.from(programsByArchitecture.entries())
            .map(([architecture, programs]) => `${architecture}: ${programs.join(", ")}`)
            .join("; ");
        return `Mismatching electrical architecture: ${architectureDetails}`;
    }

    function populateProgramIdsList(validProgramOptionsWithModelYear: any, vehiclePrograms: any) {
        const programIds: string[] = [];
        validProgramOptionsWithModelYear.forEach((option: { label: string; value: string; }) => {
            if (vehiclePrograms.includes(option.label)) {
                programIds.push(option.value);
            }
        });
        return programIds;
    }

    function getValidProgramOptionsWithModelYearProgramCode(validProgramData: APIServiceFormat, validProgramOptions: any) {
        const programDataArray = validProgramData.data || [];
        const validProgramOptionsWithModelYear = programDataArray.map((programData: any) => {
            // Find the corresponding program ID by matching programCode
            const matchingOption = validProgramOptions.find(
                (option: { label: string; value: string; }) => option.label === programData.modelYearProgramCode
            );

            return {
                label: programData.modelYearProgramCode,
                value: matchingOption?.value || ""
            };
        }).filter((option: { label: string; value: string; }) => option.value !== "");
        return validProgramOptionsWithModelYear;
    }

    async function getValidProgramOptionsAndData() {
        const validProgramOptionsResponse = await apiService.getProgramOptions([]);
        if (!validProgramOptionsResponse.isSuccess) {
            throw new Error("Failed to fetch valid vehicle programs for validation.");
        }
        const validProgramOptions = Array.isArray(validProgramOptionsResponse.data)
            ? validProgramOptionsResponse.data.map((option: { label: string; value: string; }) => ({
                label: option.label,
                value: option.value,
                electricalArchitecture: "",
            }))
            : [];

        const validtProgramsOptionsIds = validProgramOptions.map((option: { value: string; }) => option.value);
        const validProgramData = await apiService.getProgramData(validtProgramsOptionsIds);

        if (!validProgramData.isSuccess) {
            throw new Error("Failed to fetch valid program data for validation.");
        }

        const validProgramDataArray = Array.isArray(validProgramData.data) ? validProgramData.data : [];
        for (const programDataItem of validProgramDataArray) {
            const matchingOption = validProgramOptions.find(
                (option: { label: string; value: string; electricalArchitecture: string }) => option.label === programDataItem.modelYearProgramCode
            );
            if (matchingOption) {
                matchingOption.electricalArchitecture = programDataItem.electricalArchitecture || "";
            }
        }

        return { validProgramData, validProgramOptions };
    }
}


async function validateNonOverdriveMetadata ({
    data, 
    ecuCataSelectObj, 
    unregisteredClusters,
    apiService,

}:
    Readonly<{
        data: any,
        ecuCataSelectObj: ECUCataSelectObjectInterface,
        unregisteredClusters: Record<string, any>[],
        apiService: APIService,
    }>
) {
    const errorMessages: string[] = [];

    // Get programCodeModealYear set from vehiclePrograms in data
    const vehiclePrograms: string[] = data.moduleInformation.vehiclePrograms || [];
    const modelYearProgramCodeSet: Set<string> = new Set<string>(vehiclePrograms);
    
    for (const machine of data.machineList) {
    // Validate canDiagRxId in machine item level
        const machineName = machine.machineName;
        const canDiagRxId = machine.canDiagRxId;
        const machineData = await getMachineData(machineName);
        const correctCanDiagRxId = machineData?.canDiagRxId?.startsWith("0x") ? machineData?.canDiagRxId : `0x${machineData?.canDiagRxId}`;

        if (correctCanDiagRxId !== canDiagRxId) {
            errorMessages.push(`canDiagRxId mismatch for machine ${machineName}: expected ${correctCanDiagRxId}, found ${canDiagRxId}`);
        }

        const correctMachineType = "CLASSIC";
        if (machine.machineType !== correctMachineType) {
            errorMessages.push(`machineType mismatch for machine ${machineName}: expected ${correctMachineType}, found ${machine.machineType}`);
        }
        // Validate clusterType to be EXECUTABLE in cluster item level
        const machineVariants = machine.variantList || [];
        for (const variant of machineVariants) {
            const clusters = variant.clusterList || [];
            validateClusterMetadata(clusters, variant);
        }
    }

    return {
        errorMessages
    }

    async function getMachineData(machineName: any) {
        const selectedMachineOption = ecuCataSelectObj.machines.value.find(option => option.label === machineName);
        const programIds = ecuCataSelectObj.programs.value.map(option => option.value);
        const moduleId = ecuCataSelectObj.module.value?.value || "";
        const machineId = selectedMachineOption?.value || "";
        const machineDataResponse = await apiService.getMachineData(programIds, moduleId, [machineId]);
        if (!machineDataResponse.isSuccess) {
            throw new Error(`Failed to fetch machine data for validation of machine: ${machineName}`);
        }
        const machineDataArray = machineDataResponse.data || [];
        const machineData = machineDataArray.find((md: any) => md.machineId === machineId);
        return machineData;
    }

    function validateClusterMetadata(clusters: any, variant: any) {
        for (const cluster of clusters) {
            // Skip validation for unregistered clusters
            if (unregisteredClusters.some((unregCluster) => unregCluster.clusterName === cluster.clusterName &&
                unregCluster.variantName === variant.variantName
            )) {
                console.log(`Skipping validation for unregistered cluster ${cluster.clusterName} in variant ${variant.variantName}`);
                continue;
            }


            if (cluster?.clusterType !== "EXECUTABLE") {
                errorMessages.push(`clusterType mismatch for cluster ${cluster.clusterName} in variant ${variant.variantName}: expected EXECUTABLE, found ${cluster?.clusterType}`);
            }

            // At least one selection criteria must be present for each vehicle program, and should not includes invalid vehicle programs
            const selectionCriteriaList = cluster.selectionCriteriaList || [];
            const selectionCriteriaProgramCodeSet: Set<string> = new Set<string>();

            // Check each selection criteria's modelYear-programCode against vehiclePrograms
            selectionCriteriaList.forEach((sc: any) => {
                const scModelYearProgramCode = `${sc.modelYear}-${sc.programCode}`;
                if (!modelYearProgramCodeSet.has(scModelYearProgramCode)) {
                    errorMessages.push(`Invalid selection criteria in cluster ${cluster.clusterName} of variant ${variant.variantName}: Model Year - Program Code ${scModelYearProgramCode} not found in selected vehicle programs.`);
                }
                selectionCriteriaProgramCodeSet.add(scModelYearProgramCode);
            });

            const missingVehiclePrograms = vehiclePrograms.filter(
                (vehicleProgram) => !selectionCriteriaProgramCodeSet.has(vehicleProgram)
            );
            cluster.selectionCriteriaList.push(...getSelectionCriteriaList(missingVehiclePrograms));
        }
    }
}
